import {
    autoUpdate,
    flip,
    offset,
    shift,
    useFloating,
} from "@floating-ui/react";
import {
    faArrowsRotate,
    faChevronDown,
    faIndustry,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Popover } from "@headlessui/react";
import { useQuery } from "@tanstack/react-query";
import { useAnalyticsApiClient } from "../../../hooks";

interface SiteTypeFilterWrapperProps {
    filterValue?: string[];
    setFilterValue: (value?: string[]) => void;
}

export const SiteTypeFilterWrapper = ({
    filterValue,
    setFilterValue,
}: SiteTypeFilterWrapperProps) => {
    const isDefault = !filterValue;
    const analyticsApi = useAnalyticsApiClient();
    const siteTypesQuery = useQuery({
        queryKey: ["analyticsSiteTypes"],
        queryFn: async () => {
            return await analyticsApi.analyticsSitesSiteTypesRetrieve();
        },
        refetchOnWindowFocus: false,
        staleTime: Infinity,
    });

    // Floating UI setup for popover positioning
    const { refs, floatingStyles } = useFloating({
        strategy: "fixed",
        placement: "bottom-start",
        middleware: [
            offset({
                mainAxis: 4,
            }),
            shift(),
            flip(),
        ],
        whileElementsMounted: autoUpdate,
    });

    const siteTypes = siteTypesQuery.data || [];

    // Calculate display text for button
    const getDisplayText = () => {
        if (!filterValue || filterValue.length === 0) {
            return "All site types";
        }
        if (filterValue.length === 1) {
            if (filterValue[0] === "null") {
                return "Other";
            }
            const formatted = filterValue[0]
                .replaceAll("_", " ")
                .replace(/\b\w/g, (char) => char.toUpperCase());
            return formatted;
        }
        return `${filterValue.length} site types`;
    };

    // Toggle site type selection
    const toggleSiteType = (siteType: string) => {
        const current = filterValue || [];
        if (current.includes(siteType)) {
            const updated = current.filter((t) => t !== siteType);
            setFilterValue(updated.length > 0 ? updated : undefined);
        } else {
            setFilterValue([...current, siteType]);
        }
    };

    return (
        <div ref={refs.setReference}>
            <Popover>
                {({ close }) => (
                    <>
                        <Popover.Button
                            className={`
                                flex items-center gap-2 px-3 h-8 text-sm border
                                rounded bg-white hover:bg-ae-gray-100
                                ${isDefault ? "border-ae-gray-250" : "border-ae-blue-550 bg-ae-blue-100"}
                            `}
                        >
                            <FontAwesomeIcon
                                icon={faIndustry}
                                className="w-4 text-ae-blue-550"
                            />
                            <span className="text-neutral-600 hidden xl:block whitespace-nowrap">
                                {getDisplayText()}
                            </span>
                            {siteTypesQuery.isLoading ? (
                                <div className="animate-spin">
                                    <FontAwesomeIcon
                                        icon={faArrowsRotate}
                                        className="w-3"
                                    />
                                </div>
                            ) : (
                                <FontAwesomeIcon
                                    icon={faChevronDown}
                                    className="w-3"
                                />
                            )}
                        </Popover.Button>
                        <Popover.Panel
                            ref={refs.setFloating}
                            style={floatingStyles}
                            onKeyUp={(key) => {
                                if (key.code === "Enter") {
                                    close();
                                }
                            }}
                            className="max-h-96 overflow-auto text-sm rounded-md p-0.5 bg-white py-1 border-2 border-ae-slate-300 z-50 drop-shadow-lg"
                        >
                            {siteTypesQuery.isLoading ? (
                                <div className="flex items-center justify-center p-4">
                                    <FontAwesomeIcon
                                        icon={faArrowsRotate}
                                        className="w-5 animate-spin text-ae-blue-550"
                                    />
                                </div>
                            ) : siteTypesQuery.isError ? (
                                <div className="p-4 text-sm text-red-600">
                                    Error loading site types
                                </div>
                            ) : (
                                <div>
                                    {/* Unselected items */}
                                    {siteTypes.map((siteType) => {
                                        const displayName = siteType
                                            .replaceAll("_", " ")
                                            .replace(/\b\w/g, (char) =>
                                                char.toUpperCase(),
                                            );
                                        return (
                                            <label
                                                key={siteType}
                                                className="relative cursor-default select-none flex gap-2 items-center px-2 py-1 rounded text-ae-slate-900 hover:bg-ae-gray-100"
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="accent-ae-blue-500 focus:ring-0"
                                                    checked={(
                                                        filterValue ?? []
                                                    ).includes(siteType)}
                                                    onChange={() =>
                                                        toggleSiteType(siteType)
                                                    }
                                                />
                                                <span className="block truncate font-normal">
                                                    {displayName}
                                                </span>
                                            </label>
                                        );
                                    })}
                                    <label
                                        key="null"
                                        className="relative cursor-default select-none flex gap-2 items-center px-2 py-1 rounded text-ae-slate-900 hover:bg-ae-gray-100"
                                    >
                                        <input
                                            type="checkbox"
                                            className="accent-ae-blue-500 focus:ring-0"
                                            checked={filterValue?.includes(
                                                "null",
                                            )}
                                            onChange={() =>
                                                toggleSiteType("null")
                                            }
                                        />
                                        <span className="block truncate font-normal">
                                            Other
                                        </span>
                                    </label>
                                </div>
                            )}
                        </Popover.Panel>
                    </>
                )}
            </Popover>
        </div>
    );
};
